import { zodResolver } from '@hookform/resolvers/zod';
import {
  Rillet,
  RilletCoaListResponse,
  RilletEnvEnum,
  RilletFormSchema,
  RilletFormType,
} from '@monetize/types/app';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdRefresh } from 'react-icons/md';
import { useSaveRilletConfiguration } from '../../../../../api/accountingService';
import {
  MButton,
  MDivider,
  MFormField,
  MGrid,
  MGridItem,
  MHStack,
  MIconButton,
  MInput,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { logger } from '../../../../../services/logger';
import { RILLET_FORM_FIELDS } from './accounting.constants';

const ENVIRONMENT_OPTIONS = [
  { value: RilletEnvEnum.SANDBOX, title: 'Sandbox' },
  { value: RilletEnvEnum.PRODUCTION, title: 'Production' },
];

interface RilletFormProps {
  connection?: Rillet | null;
  coaData?: RilletCoaListResponse;
  isRefetchingCoa?: boolean;
  onClose: () => void;
  refetchCoaData?: () => void;
}

export const RilletForm = ({
  connection,
  coaData,
  isRefetchingCoa,
  onClose,
  refetchCoaData,
}: RilletFormProps) => {
  const saveRilletConfiguration = useSaveRilletConfiguration({
    meta: { showErrorToast: true },
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isDirty },
  } = useForm<RilletFormType>({
    resolver: zodResolver(RilletFormSchema),
    mode: 'onChange',
    defaultValues: {
      apiKey: connection?.apiKey ?? '',
      environment: connection?.environment ?? RilletEnvEnum.SANDBOX,
      defaultPaymentAccountCode: connection?.defaultPaymentAccountCode ?? null,
    },
  });

  const paymentCoaOptions = useMemo(() => {
    return (
      coaData?.paymentCoas?.map((coa) => ({
        value: coa.code,
        title: `${coa.code} - ${coa.name}`,
      })) ?? []
    );
  }, [coaData?.paymentCoas]);

  const onSubmitHandler = async (data: RilletFormType) => {
    const payload = {
      apiKey: data.apiKey,
      environment: data.environment,
      defaultPaymentAccountCode: data.defaultPaymentAccountCode ?? null,
    };

    await saveRilletConfiguration.mutateAsync(payload);

    onClose();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitHandler, (error) => logger.error(error))}
      style={{ width: '100%' }}
    >
      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={12}>
          <MFormField
            error={errors.apiKey}
            label={RILLET_FORM_FIELDS.apiKey.label}
            isRequired
          >
            <MInput {...register('apiKey')} placeholder="Enter API Key" />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.environment}
            label={RILLET_FORM_FIELDS.environment.label}
            isRequired
          >
            <Controller
              name="environment"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  {...field}
                  placeholder="Select Environment"
                  items={ENVIRONMENT_OPTIONS}
                  value={field.value ?? ''}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        {connection && (
          <MGridItem colSpan={6}>
            <MFormField
              error={errors.defaultPaymentAccountCode}
              label={RILLET_FORM_FIELDS.defaultPaymentAccountCode.label}
              tooltip={RILLET_FORM_FIELDS.defaultPaymentAccountCode.tooltip}
              labelRightComponent={
                refetchCoaData ? (
                  <MIconButton
                    aria-label="Refresh COA data"
                    icon={<MdRefresh />}
                    fontSize={18}
                    size="sm"
                    variant="ghost"
                    onClick={refetchCoaData}
                    isLoading={isRefetchingCoa}
                    ml={1}
                    mt={-2}
                  />
                ) : undefined
              }
            >
              <Controller
                name="defaultPaymentAccountCode"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    {...field}
                    placeholder="Select Payment COA"
                    items={paymentCoaOptions}
                    value={field.value ?? ''}
                    onChange={(selectedCode) => {
                      field.onChange(selectedCode || null);
                    }}
                  />
                )}
              />
            </MFormField>
          </MGridItem>
        )}
      </MGrid>
      <MDivider my={4} />
      <MHStack gap="4" justify="end" align="center">
        <MButton variant="cancel" onClick={onClose}>
          Cancel
        </MButton>
        <MButton
          type="submit"
          variant="primary"
          isLoading={saveRilletConfiguration.isPending}
          isDisabled={saveRilletConfiguration.isPending || !isDirty}
        >
          {connection ? 'Save' : 'Connect'}
        </MButton>
      </MHStack>
    </form>
  );
};
